<template>
  <InViewport @enter="enterViewport" @leave="leaveViewport">
    <div class="gmf-context" @mousedown.passive="stopAutoSlide" @click.passive="stopAutoSlide">
      <div class="gmf-start-page-slideshow-wrapper">
        <div class="gmf-start-page-slideshow-header" v-if="slideshowTitlesEnabled && hasSlideTranslations">
          <Transition name="gmf-slideshow-header" mode="out-in">
            <h3 class="gmf-header" :key="slide">{{ t('start_page_slideshow.'+slides[slide]) }}</h3>
          </Transition>
        </div>
        <div class="gmf-start-page-slideshow-anchor" :class="{ 'gmf-has-buttons': showMoreButtons }" ref="anchor">
          <template v-for="(s, i) in slides" :key="s">
            <div v-if="s === 'new_comment_slideshow'" class="gmf-start-page-slideshow-slide gmf-new-comments-slideshow"
              :class="{ 'gmf-active': slide == i }">
              <NewCommentsSlideshow :compactMobileView="useCompactMobileView" commentType="product_review,product_hype,product_question,social_post" :hideButton="true" />
              <button class="gmf-btn gmf-btn-sm gmf-btn-primary" @click.stop.prevent="openSocialFeed()" v-if="showMoreButtons">{{ t('generic.show_more') }}</button>
            </div>
            <div v-else-if="s === 'active_members_grid'" class="gmf-start-page-slideshow-slide gmf-active-members-grid"
              :class="{ 'gmf-active': slide == i }">
              <ActiveMembersGrid v-if="visitedSlides[i]" :manualStart="true" :inViewport="inViewport && visitedSlides[i]" />
              <button class="gmf-btn gmf-btn-sm gmf-btn-primary" @click.stop.prevent="openSocialFeed()" v-if="showMoreButtons">{{ t('generic.show_more') }}</button>
            </div>
            <div v-else-if="s === 'compact_leaderboard' && use3DAvatar && useFullbodyAvatar"
              class="gmf-start-page-slideshow-slide gmf-fullbody-leaderboard"
              :class="{ 'gmf-active': slide == i }">
              <FullbodyLeaderboard v-if="visitedSlides[i]" />
              <button class="gmf-btn gmf-btn-sm gmf-btn-primary" @click.stop.prevent="openSocialFeed()" v-if="showMoreButtons">{{ t('generic.show_more') }}</button>
            </div>
            <div v-else-if="s === 'compact_leaderboard'" class="gmf-start-page-slideshow-slide gmf-compact-leaderboard"
              :class="{ 'gmf-active': slide == i }">
              <CompactLeaderboard v-if="visitedSlides[i]" />
              <button class="gmf-btn gmf-btn-sm gmf-btn-primary" @click.stop.prevent="openSocialFeed()" v-if="showMoreButtons">{{ t('generic.show_more') }}</button>
            </div>
            <div v-else-if="s === 'recent_images_grid'" class="gmf-start-page-slideshow-slide gmf-recent-images-grid"
              :class="{ 'gmf-active': slide == i }">
              <RecentImagesGrid :compactMobileView="useCompactMobileView" />
              <button class="gmf-btn gmf-btn-sm gmf-btn-primary" @click.stop.prevent="openSocialFeed({ has_images: 1 })" v-if="showMoreButtons">{{ t('generic.show_more') }}</button>
            </div>
            <div v-else-if="s === 'pinned_post'" class="gmf-start-page-slideshow-slide"
              :class="{ 'gmf-active': slide == i }" style="align-self: stretch">
              <div class="gmf-pinned-post">
                <PinnedPost :post="pinnedPost" />
              </div>
            </div>
            <div v-else-if="s === 'quest_highlight'" class="gmf-start-page-slideshow-slide"
              :class="{ 'gmf-active': slide == i }" style="align-self: stretch">
              <QuestHighlightSlide :quest="questHighlight" />
              <button class="gmf-btn gmf-btn-sm gmf-btn-primary" @click.stop.prevent="openSocialFeed()" v-if="showMoreButtons">{{ t('generic.show_more') }}</button>
            </div>
            <div v-else-if="s === 'social_banner'" :class="{ 'gmf-active': slide == i }" class="gmf-start-page-slideshow-slide gmf-banner-slide-wrap">
              <SocialBanner />
            </div>
          </template>
        </div>
      </div>
      <div class="gmf-center gmf-mt-4" v-if="slides.length > 1">
        <NavDots :count="slides.length" :modelValue="slide" @update:modelValue="v => setSlide(v)" />
      </div>
    </div>
  </InViewport>
</template>

<style>
.gmf-start-page-slideshow-wrapper {
  width: 100%;
  margin: -20px 0;
  padding: 20px 0;
  overflow: visible;
  clip-path: inset(-100% 0 -100% 0);  /* Allow overflow on both top and bottom */
}

.gmf-start-page-slideshow-anchor {
  display: flex;
  align-items: stretch;
  gap: var(--gmf-object-spacing);
  width: 100%;
  overflow-x: scroll;
  overflow-y: hidden;  /* Prevent vertical scrolling */
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scroll-snap-stop: always;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  /* allow overflow on top */
  margin-top: -50px;
  padding-top: 50px;
}

.gmf-start-page-slideshow-anchor::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.gmf-context .gmf-start-page-slideshow-header {
  padding: 0;
  width: 100%;
  margin-bottom: var(--gmf-p-3);
  margin-top: -5px;
  text-align: center;
  position: relative;
}

.gmf-context .gmf-start-page-slideshow-header .gmf-header {
  margin-bottom: 0;
  width: 100%;
}

.gmf-slideshow-header-enter-active,
.gmf-slideshow-header-leave-active {
  opacity: 1;
  transition: all 0.2s;
}

.gmf-slideshow-header-enter-from,
.gmf-slideshow-header-leave-to {
  opacity: 0;
  transition: all 0.2s;
}

.gmf-start-page-slideshow-slide {
  align-self: center;
  max-height: 100%;
  width: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  /*opacity: 1;
  transition: opacity 0.4s linear;*/
  position: relative;
  scroll-snap-align: start;
  scroll-snap-stop: always;
}

.gmf-start-page-slideshow-slide.gmf-active {
  /*opacity: 1;*/
}

.gmf-start-page-slideshow-slide.gmf-active-members-grid {
  align-self: stretch;
  height: auto;
  /*padding: 0px 20px;*/
}

.gmf-start-page-slideshow-slide .gmf-pinned-post {
  position: absolute;
  inset: 0;
}

/* button hell, remove if buttons are removed */
.gmf-start-page-slideshow-slide {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gmf-p-4);
  align-self: stretch;
  justify-content: space-between;
}
.gmf-start-page-slideshow-slide .gmf-btn {
  z-index: 1;
  margin: 0;
}
.gmf-start-page-slideshow-slide .gmf-fullbody-leaderboard-section, .gmf-start-page-slideshow-slide .gmf-compact-leaderboard-section, .gmf-start-page-slideshow-slide .gmf-recent-images-grid-wrapper {
  margin: auto;
}
@media (min-width: 600px) {
  /* Remove the bottom margin from the active members grid on desktop, to increase chance of fitting 2 rows of avatars */
  /* only if the slides have show more buttons */
  .gmf-has-buttons .gmf-start-page-slideshow-slide .gmf-active-members-grid-wrap {
    margin-bottom: calc(0px - var(--gmf-p-4));
  }
}

.gmf-start-page-slideshow-slide.gmf-recent-images-grid {
  align-self: stretch;
  height: auto;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}
.gmf-start-page-slideshow-slide .gmf-recent-images-grid-wrapper {
  max-width: 1080px;
}
</style>

<script>
import state from '@/state';
const t = state.t;

import ActiveMembersGrid from '@/widgets/ActiveMembersGrid';
import NewCommentsSlideshow from '@/widgets/NewCommentsSlideshow';
import CompactLeaderboard from '@/widgets/CompactLeaderboard';
import FullbodyLeaderboard from '@/widgets/FullbodyLeaderboard';
import NavDots from '@/components/NavDots';
import RecentImagesGrid from '@/widgets/RecentImagesGrid';
import InViewport from '@/components/InViewport';
import PinnedPost from '@/widgets/PinnedPost/index.vue';
import QuestHighlightSlide from './QuestHighlightSlide.vue';
import SocialBanner from './SocialBanner.vue';

import openSocialModal from '@/widgets/SocialFeed/openSocialModal';
import callApi from '@/utils/callApi';
import eventbus from '@/utils/eventbus';

export default {
  name: 'StartPageSlideshow',
  provide() {
    return {
      injectedLeaderboard: null,
    };
  },
  beforeMount() {
    this.slide = 0;

    this.visitedSlides[this.slide] = true;
  },
  async mounted() {
    eventbus.on('startPageSlideshow:start', this.startAutoSlide);
    eventbus.on('startPageSlideshow:stop', this.stopAutoSlide);

    // Add scroll event listener
    this.$refs.anchor.addEventListener('scroll', this.handleScroll);

    if (!state.merchantConfig.widgets.startPageSlideshow.showPinnedPostSlide) return;

    if (state.merchantConfig.widgets.startPageSlides.includes("quest_highlight")) {
      this.loadQuestHighlight();
    }
    const res = await callApi('GET', state.commentSearchEndpoint + `?take=1&no_count=1&reply_depth=0&pin=start_page&translate=${this.translationsEnabled}`);

    const { comments } = await res.json();

    const comment = comments.find(c => c.pins && c.pins.length > 0 && c.pins.includes('start_page'));

    if (comment) {
      this.pinnedPost = comment;
    }
  },
  data() {
    const visitedSlides = [];

    // With this we can wait with loading some of the widgets in order to make fewer requests on page load.
    // Latest comments and recent images are force enabled in since they determine the max height of this widget
    // If we set a fixed height, all four widgets can be lazy loaded
    for (let i = 0; i < 10; ++i) {
      visitedSlides.push(false);
    }

    return {
      inViewport: false,
      slide: 0,
      autoSlideInterval: null,
      visitedSlides,
      pinnedPost: null,
      questHighlight: null,
      isProgrammaticScroll: false,
    };
  },
  beforeUnmount() {
    eventbus.off('startPageSlideshow:start', this.startAutoSlide);
    eventbus.off('startPageSlideshow:stop', this.stopAutoSlide);
    
    // Remove scroll event listener
    this.$refs.anchor.removeEventListener('scroll', this.handleScroll);

    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = null;
    }
  },
  methods: {
    t,
    openSocialFeed(filter = null) {
      openSocialModal({
        displayHeader: true,
        filter,
      });
    },
    async loadQuestHighlight () {
        const res = await callApi('GET', '/v1/client/quests');
        const data = await res.json();
        this.quests = data.quests;
        this.questHighlight = this.quests.filter(q => !q.achievedAt)[0];
    },
    enterViewport() {
      this.inViewport = true;
      this.startAutoSlide();
    },
    leaveViewport() {
      this.inViewport = false;
      this.stopAutoSlide();
    },
    startAutoSlide() {
      if (!this.inViewport) {
        return;
      }
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval);
      }
      this.autoSlideInterval = setInterval(this.autoslide, 5000);
    },
    stopAutoSlide() {
      if (this.autoSlideInterval) {
        clearInterval(this.autoSlideInterval);
        this.autoSlideInterval = null;
      }
      // mark adjacent slides as visited
      this.visitedSlides[this.slide - 1] = true;
      this.visitedSlides[this.slide + 1] = true;
    },
    setSlide(slide, stopAutoSlide = true) {
      if (stopAutoSlide) {
        this.stopAutoSlide();
      }
      if (slide < 0) {
        slide = 0;
      } else if (slide >= this.slides.length) {
        slide = this.slides.length - 1;
      }
      this.visitedSlides[slide] = true;
      
      this.isProgrammaticScroll = true;
      const anchor = this.$refs.anchor;
      anchor.scrollTo({
        left: anchor.offsetWidth * slide,
        behavior: 'smooth'
      });
      
      setTimeout(() => {
        this.isProgrammaticScroll = false;
      }, 500);
    },
    autoslide() {
      let nextSlide = this.slide + 1;
      if (nextSlide >= this.slides.length) {
        nextSlide = 0;
      }
      this.setSlide(nextSlide, false);
    },
    handleScroll() {
      const anchor = this.$refs.anchor;
      const currentSlide = Math.round(anchor.scrollLeft / anchor.offsetWidth);
      if (currentSlide !== this.slide) {
        this.slide = currentSlide;
        this.visitedSlides[this.slide] = true;
        
        if (!this.isProgrammaticScroll) {
          this.stopAutoSlide();
        }
      }
    },
  },
  computed: {
    showMoreButtons() {
      return state.merchantConfig.widgets.startPageSlideshow?.showMoreButtons;
    },
    slides() {
      const slides = state.merchantConfig.widgets.startPageSlides.slice(0);
      /*[
        'new_comment_slideshow',
        'active_members_grid',
        'compact_leaderboard',
        'recent_images_grid',
        'pinned_post',
      ];*/
      if (! (this.pinnedPost && this.pinnedPost.content) && slides.indexOf('pinned_post') !== -1) {
        slides.splice(slides.indexOf('pinned_post'), 1);
      }
      if (! this.questHighlight && slides.indexOf('quest_highlight') !== -1) {
        slides.splice(slides.indexOf('quest_highlight'), 1);
      }
      return slides;
    },
    useCompactMobileView() {
      return state.merchantConfig.widgets.startPageSlideshow.compactOnMobile;
    },
    hasSlideTranslations() {
      return this.slides.some(translation => !!t(translation));
    },
    slideshowTitlesEnabled() {
      return state.merchantConfig.widgets.startPageSlideshow.showTitles;
    },
    translationsEnabled() {
      return state.merchantConfig?.widgets?.translations.mode !== 'translateNothing';
    },
    use3DAvatar() {
      return state.merchantConfig?.avatars?.enabled === '3D';
    },
    useFullbodyAvatar() {
      return state.merchantConfig?.avatars?.fullbodyStartpage;
    }
  },
  components: {
    NavDots,
    ActiveMembersGrid,
    NewCommentsSlideshow,
    CompactLeaderboard,
    FullbodyLeaderboard,
    RecentImagesGrid,
    InViewport,
    PinnedPost,
    QuestHighlightSlide,
    SocialBanner,
  },
};
</script>
